import React from 'react';
import { User, Mail, Phone, MapPin, Search, ChevronDown, UserPlus } from 'lucide-react';
import { AddressAutocompleteInput } from '../../../components/AddressAutocompleteInput';
import {
  CONTACT_TYPES_SELECTABLE_FOR_LEAD,
  type ContactType,
  contactDisplayName,
  contactTypeClearsCompany,
  contactTypeRequiresCompany,
  contactTypeRequiresLastName,
  isContactSelectableForLeadInquiry,
} from '../../../types';

export type TenderCustomerFieldKey =
  | 'title'
  | 'first_name'
  | 'last_name'
  | 'company'
  | 'email'
  | 'phone'
  | 'contact_address'
  | 'contact_type'
  | 'customer_street'
  | 'customer_zip'
  | 'customer_city'
  | 'deadline'
  | 'execution'
  | 'binding_period_days';

export interface TenderCustomerFields {
  title: string;
  first_name: string;
  last_name: string;
  company: string;
  email: string;
  phone: string;
  contact_address: string;
  contact_type: string;
  customer_street: string;
  customer_zip: string;
  customer_city: string;
  deadline: string;
  execution: string;
  binding_period_days: string;
}

const inputClass =
  'w-full px-3 py-2 bg-app-surface border border-app-border rounded-lg focus:ring-1 focus:ring-pipedrive-blue outline-none transition-all text-sm text-app-text';

/** Pflichtfeld, das beim Weiter fehlte. `nonce` lässt das Blinken bei jedem Klick neu starten. */
export interface TenderCustomerFieldError {
  key: 'company' | 'last_name';
  message: string;
  nonce: number;
}

/**
 * Gleiche Pflichtfeld-Regeln wie der Server (`upsertContactForAusschreibung`), damit der Fehler
 * am Feld steht statt als Banner über der Seite. Nur für „Neuen Kontakt erstellen“.
 */
export function validateTenderNewContact(
  values: TenderCustomerFields,
): Omit<TenderCustomerFieldError, 'nonce'> | null {
  const ct = (values.contact_type || 'Gewerblicher Kunde') as ContactType;
  if (contactTypeRequiresCompany(ct) && !values.company.trim()) {
    return { key: 'company', message: 'Bitte den Unternehmensnamen angeben.' };
  }
  if (contactTypeRequiresLastName(ct) && !values.last_name.trim()) {
    return { key: 'last_name', message: 'Bitte den Nachnamen angeben.' };
  }
  return null;
}

type ContactListRow = {
  id: string;
  name: string;
  first_name?: string | null;
  last_name?: string | null;
  email: string;
  phone: string;
  address: string;
  company?: string | null;
  contact_type?: string | null;
};

export interface TenderCustomerFormProps {
  values: TenderCustomerFields;
  onChange: (patch: Partial<TenderCustomerFields>) => void;
  selectedContactId: string | null;
  newContactChosen: boolean;
  onContactModeChange: (next: { selectedContactId: string | null; newContactChosen: boolean }) => void;
  contactPickError?: string;
  /** Fehlendes Pflichtfeld: wird rot markiert, blinkt kurz und bekommt den Fokus. */
  fieldError?: TenderCustomerFieldError | null;
  /**
   * Eine Spalte statt sm:2 — für schmale Seitenleisten, sonst nutzt `sm:` den Viewport
   * und quetscht zwei Spalten in ~320px.
   */
  narrowRail?: boolean;
}

export const TenderCustomerForm: React.FC<TenderCustomerFormProps> = ({
  values,
  onChange,
  selectedContactId,
  newContactChosen,
  onContactModeChange,
  contactPickError,
  fieldError,
  narrowRail = false,
}) => {
  const companyRef = React.useRef<HTMLInputElement>(null);
  const lastNameRef = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    if (!fieldError) return;
    const el = fieldError.key === 'company' ? companyRef.current : lastNameRef.current;
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    el.focus({ preventScroll: true });
    // Blinken bei jedem Klick auf „Weiter“ neu starten (Klasse entfernen, Reflow, wieder setzen).
    el.classList.remove('tender-field-error-blink');
    void el.offsetWidth;
    el.classList.add('tender-field-error-blink');
    const done = () => el.classList.remove('tender-field-error-blink');
    el.addEventListener('animationend', done, { once: true });
    return () => el.removeEventListener('animationend', done);
  }, [fieldError]);

  /** Roter Rahmen bleibt, bis das Feld ausgefüllt ist. */
  const errorInputClass = (key: TenderCustomerFieldError['key']) =>
    fieldError?.key === key ? '!border-red-500 focus:!ring-red-500' : '';
  const errorMessage = (key: TenderCustomerFieldError['key']) =>
    fieldError?.key === key ? (
      <p className="text-sm text-red-600 mt-1" role="alert">
        {fieldError.message}
      </p>
    ) : null;

  const update = (key: TenderCustomerFieldKey, value: string) => {
    onChange({ [key]: value } as Partial<TenderCustomerFields>);
  };

  const grid2 = narrowRail ? 'grid grid-cols-1 gap-4' : 'grid gap-4 sm:grid-cols-2';
  const span2 = narrowRail ? '' : 'sm:col-span-2';
  const span1 = narrowRail ? '' : 'sm:col-span-1';

  const [contacts, setContacts] = React.useState<ContactListRow[]>([]);
  const [contactOpen, setContactOpen] = React.useState(false);
  const [contactSearch, setContactSearch] = React.useState('');
  const contactDropdownRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    fetch('/api/contacts', { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : []))
      .then((list) => {
        const raw = Array.isArray(list) ? list : [];
        const mapped: ContactListRow[] = raw
          .filter((c: { contact_type?: string | null }) => isContactSelectableForLeadInquiry(c.contact_type))
          .map((c: Record<string, unknown>) => ({
            id: String(c.id ?? ''),
            name: String(c.name ?? ''),
            first_name: (c.first_name as string | null) ?? null,
            last_name: (c.last_name as string | null) ?? null,
            email: String(c.email ?? ''),
            phone: String(c.phone ?? ''),
            address: String(c.address ?? ''),
            company: (c.company as string | null) ?? null,
            contact_type: (c.contact_type as string | null) ?? null,
          }))
          .filter((c) => c.id.length > 0);
        setContacts(mapped);
      })
      .catch(() => setContacts([]));
  }, []);

  React.useEffect(() => {
    if (!contactOpen) return;
    const onOutside = (e: MouseEvent) => {
      if (contactDropdownRef.current && !contactDropdownRef.current.contains(e.target as Node)) {
        setContactOpen(false);
      }
    };
    document.addEventListener('mousedown', onOutside);
    return () => document.removeEventListener('mousedown', onOutside);
  }, [contactOpen]);

  const showContactDetailFields = selectedContactId != null || newContactChosen;

  const selectedContact =
    selectedContactId != null ? contacts.find((c) => c.id === selectedContactId) : null;

  const contactDisplayLabel = newContactChosen
    ? 'Neuen Kontakt erstellen'
    : selectedContact
      ? `${contactDisplayName(selectedContact)}${selectedContact.email ? ` · ${selectedContact.email}` : ''}`.trim() ||
        `Kontakt ${selectedContactId.slice(0, 8)}…`
      : 'Kontakt auswählen';

  const filteredContacts = React.useMemo(() => {
    const q = contactSearch.trim().toLowerCase();
    if (!q) return contacts;
    return contacts.filter(
      (c) =>
        (c.name || '').toLowerCase().includes(q) ||
        (c.first_name || '').toLowerCase().includes(q) ||
        (c.last_name || '').toLowerCase().includes(q) ||
        (c.email || '').toLowerCase().includes(q) ||
        (c.phone || '').toLowerCase().includes(q) ||
        (c.address || '').toLowerCase().includes(q) ||
        (c.company || '').toLowerCase().includes(q),
    );
  }, [contacts, contactSearch]);

  const handleSelectContact = (c: ContactListRow) => {
    onContactModeChange({ selectedContactId: c.id, newContactChosen: false });
    setContactOpen(false);
    setContactSearch('');
  };

  const handleSelectNewContact = () => {
    onContactModeChange({ selectedContactId: null, newContactChosen: true });
    onChange({
      contact_type: 'Gewerblicher Kunde',
      first_name: '',
      last_name: '',
      company: '',
      email: '',
      phone: '',
      contact_address: '',
    });
    setContactOpen(false);
    setContactSearch('');
  };

  const ct = (values.contact_type || 'Gewerblicher Kunde') as ContactType;

  return (
    <div className={narrowRail ? 'space-y-4' : 'space-y-6'}>
      <div className={grid2}>
        <div className={`${span2} space-y-1`.trim()}>
          <label htmlFor="tender-title" className="block text-xs font-medium text-app-text-muted">
            Ausschreibungstitel
          </label>
          <input
            id="tender-title"
            type="text"
            value={values.title}
            onChange={(e) => update('title', e.target.value)}
            placeholder="z. B. Sanierung Musterstraße"
            className={inputClass}
          />
        </div>

        <div className={`${span2} space-y-1`.trim()} ref={contactDropdownRef}>
          <label className="block text-xs font-medium text-app-text-muted">
            Kontaktauswahl <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <button
              type="button"
              onClick={() => setContactOpen((o) => !o)}
              className={`w-full flex items-center justify-between gap-2 px-3 py-2 text-left text-sm text-app-text bg-app-surface border rounded-lg focus:ring-2 focus:ring-pipedrive-blue/20 focus:border-pipedrive-blue outline-none ${
                contactPickError ? 'border-red-400' : 'border-app-border'
              }`}
            >
              <span className="truncate min-w-0 text-left">{contactDisplayLabel}</span>
              <ChevronDown className="w-4 h-4 shrink-0 text-app-text-subtle" />
            </button>
            {contactOpen && (
              <div className="absolute top-full left-0 right-0 z-50 mt-1 bg-app-surface border border-app-border rounded-lg shadow-lg overflow-hidden flex flex-col max-h-64">
                <div className="p-2 border-b border-app-border">
                  <div className="relative">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <input
                      type="text"
                      value={contactSearch}
                      onChange={(e) => setContactSearch(e.target.value)}
                      placeholder="Suchen..."
                      className="w-full pl-8 pr-3 py-1.5 text-sm border border-app-border rounded-md focus:ring-1 focus:ring-pipedrive-blue outline-none"
                    />
                  </div>
                </div>
                <button
                  type="button"
                  onClick={handleSelectNewContact}
                  className={`w-full shrink-0 text-left px-3 py-2.5 text-sm font-medium border-b border-app-border flex items-center gap-2 transition-colors ${
                    newContactChosen
                      ? 'bg-pipedrive-blue text-white'
                      : 'text-pipedrive-blue hover:bg-sky-50'
                  }`}
                >
                  <UserPlus className="w-4 h-4 shrink-0" />
                  Neuen Kontakt erstellen
                </button>
                <div className="overflow-y-auto flex-1 min-h-0">
                  {filteredContacts.map((c) => (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => handleSelectContact(c)}
                      className={`w-full text-left px-3 py-2 text-sm transition-colors ${
                        selectedContactId === c.id
                          ? 'bg-pipedrive-blue text-white'
                          : 'text-app-text hover:bg-app-hover'
                      }`}
                    >
                      {contactDisplayName(c)}
                      {c.email ? ` · ${c.email}` : ''}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
          {contactPickError && <p className="text-sm text-red-600 mt-1">{contactPickError}</p>}
        </div>

        {showContactDetailFields && newContactChosen && (
          <div className={`${span2} flex flex-col gap-2`.trim()}>
            <label className="block text-xs font-medium text-app-text-muted">
              Kontaktart <span className="text-red-500">*</span>
            </label>
            <div
              className={`flex w-full rounded-lg border border-app-border bg-app-surface-muted/80 p-0.5 gap-0.5 ${
                narrowRail ? 'flex-col' : 'flex-wrap'
              }`}
              role="group"
              aria-label="Kontaktart"
            >
              {CONTACT_TYPES_SELECTABLE_FOR_LEAD.map((t) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => {
                    onChange({
                      contact_type: t,
                      company: contactTypeClearsCompany(t) ? '' : values.company,
                    });
                  }}
                  className={`${
                    narrowRail ? 'w-full' : 'flex-1 min-w-[7.5rem]'
                  } px-2 py-2 text-sm font-medium rounded-md transition-all leading-tight ${
                    ct === t
                      ? 'bg-app-surface text-app-text shadow-sm ring-1 ring-slate-200/80'
                      : 'text-app-text-muted hover:text-app-text hover:bg-app-hover/80'
                  }`}
                >
                  {t}
                </button>
              ))}
            </div>
          </div>
        )}

        {showContactDetailFields && newContactChosen && ct === 'Privatkunde' && (
          <>
            <div className={`space-y-1 ${span1}`.trim()}>
              <label className="block text-xs font-medium text-app-text-muted">Vorname</label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type="text"
                  value={values.first_name}
                  onChange={(e) => update('first_name', e.target.value)}
                  className={`${inputClass} pl-10`}
                  placeholder="z. B. Max"
                />
              </div>
            </div>
            <div className={`space-y-1 ${span1}`.trim()}>
              <label className="block text-xs font-medium text-app-text-muted">
                Nachname <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  ref={lastNameRef}
                  type="text"
                  value={values.last_name}
                  onChange={(e) => update('last_name', e.target.value)}
                  className={`${inputClass} pl-10 ${errorInputClass('last_name')}`.trim()}
                  placeholder="z. B. Mustermann"
                  aria-invalid={fieldError?.key === 'last_name' || undefined}
                />
              </div>
              {errorMessage('last_name')}
            </div>
          </>
        )}

        {showContactDetailFields && newContactChosen && ct === 'Gewerblicher Kunde' && (
          <>
            <div className={`${span2} space-y-1`.trim()}>
              <label className="block text-xs font-medium text-app-text-muted">
                Unternehmen <span className="text-red-500">*</span>
              </label>
              <input
                ref={companyRef}
                type="text"
                value={values.company}
                onChange={(e) => update('company', e.target.value)}
                className={`${inputClass} ${errorInputClass('company')}`.trim()}
                placeholder="z. B. Muster GmbH"
                aria-invalid={fieldError?.key === 'company' || undefined}
              />
              {errorMessage('company')}
            </div>
            <div className={span2}>
              <p className="text-sm font-medium text-app-text-muted">Ansprechpartner</p>
            </div>
            <div className={`space-y-1 ${span1}`.trim()}>
              <label className="block text-xs font-medium text-app-text-muted">Vorname</label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type="text"
                  value={values.first_name}
                  onChange={(e) => update('first_name', e.target.value)}
                  className={`${inputClass} pl-10`}
                  placeholder="z. B. Max"
                />
              </div>
            </div>
            <div className={`space-y-1 ${span1}`.trim()}>
              <label className="block text-xs font-medium text-app-text-muted">Nachname</label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type="text"
                  value={values.last_name}
                  onChange={(e) => update('last_name', e.target.value)}
                  className={`${inputClass} pl-10`}
                  placeholder="z. B. Mustermann"
                />
              </div>
            </div>
          </>
        )}

        {showContactDetailFields && newContactChosen && (
          <>
            <div className={`space-y-1 ${span1}`.trim()}>
              <label className="block text-xs font-medium text-app-text-muted">E-Mail</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type="email"
                  value={values.email}
                  onChange={(e) => update('email', e.target.value)}
                  className={`${inputClass} pl-10`}
                  placeholder="kunde@beispiel.de"
                />
              </div>
            </div>
            <div className={`space-y-1 ${span1}`.trim()}>
              <label className="block text-xs font-medium text-app-text-muted">Telefon</label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type="tel"
                  value={values.phone}
                  onChange={(e) => update('phone', e.target.value)}
                  className={`${inputClass} pl-10`}
                  placeholder="+49 123 456789"
                />
              </div>
            </div>
            <div className={`${span2} space-y-1`.trim()}>
              <label className="block text-xs font-medium text-app-text-muted" htmlFor="tender-contact-address">
                Adresse (Kontakt)
              </label>
              <div className="relative">
                <MapPin className="absolute left-3 top-[14px] z-[1] w-4 h-4 text-slate-400 pointer-events-none" />
                <AddressAutocompleteInput
                  id="tender-contact-address"
                  value={values.contact_address}
                  onChange={(v) => update('contact_address', v)}
                  placeholder="Straße, Hausnummer, PLZ Ort"
                  inputClassName={`${inputClass} pl-10`}
                />
              </div>
            </div>
          </>
        )}
      </div>

      <div className="space-y-3">
        <p className="text-sm font-medium text-app-text">Baustelle / Objekt</p>
        <div className={grid2}>
          <div className={`${span2} space-y-1`.trim()}>
            <label htmlFor="customer-street" className="block text-xs font-medium text-app-text-muted">
              Straße
            </label>
            <input
              id="customer-street"
              type="text"
              value={values.customer_street}
              onChange={(e) => update('customer_street', e.target.value)}
              placeholder="Musterstraße 12"
              className={inputClass}
            />
          </div>
          <div className="space-y-1">
            <label htmlFor="customer-zip" className="block text-xs font-medium text-app-text-muted">
              PLZ
            </label>
            <input
              id="customer-zip"
              type="text"
              value={values.customer_zip}
              onChange={(e) => update('customer_zip', e.target.value)}
              placeholder="12345"
              className={inputClass}
            />
          </div>
          <div className="space-y-1">
            <label htmlFor="customer-city" className="block text-xs font-medium text-app-text-muted">
              Ort
            </label>
            <input
              id="customer-city"
              type="text"
              value={values.customer_city}
              onChange={(e) => update('customer_city', e.target.value)}
              placeholder="Musterstadt"
              className={inputClass}
            />
          </div>
        </div>
      </div>

      <div className={grid2}>
        <div className="space-y-1">
          <label htmlFor="deadline" className="block text-xs font-medium text-app-text-muted">
            Abgabefrist
          </label>
          <input
            id="deadline"
            type="date"
            value={values.deadline}
            onChange={(e) => update('deadline', e.target.value)}
            className={inputClass}
          />
        </div>
        <div className="space-y-1">
          <label htmlFor="execution" className="block text-xs font-medium text-app-text-muted">
            Ausführungszeitraum
          </label>
          <input
            id="execution"
            type="text"
            value={values.execution}
            onChange={(e) => update('execution', e.target.value)}
            placeholder="z. B. Q2 2026"
            className={inputClass}
          />
        </div>
        <div className="space-y-1">
          <label htmlFor="binding" className="block text-xs font-medium text-app-text-muted">
            Bindefrist (Tage)
          </label>
          <input
            id="binding"
            type="number"
            min={0}
            value={values.binding_period_days}
            onChange={(e) => update('binding_period_days', e.target.value)}
            placeholder="z. B. 30"
            className={inputClass}
          />
        </div>
      </div>
    </div>
  );
};
